import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import sortBy from 'lodash/sortBy';
import { Grid, Field } from 'nallian-shared-ui/lib/components';
import lookupService from 'nallian-shared-ui/lib/hoc/lookupService';
import set from 'lodash/set';
import { compose } from 'redux';
import {
  InputFieldValueComponent,
  SelectFieldValueComponent,
} from 'nallian-shared-ui/lib/components/redux-form-semantic-ui';
import { FormModal } from 'nallian-shared-ui/lib/modules';
import { generateErrorsForRequiredFields } from '../../../app/lib/validationHelper';
import { normalizeTag } from '../../../app/helpers/normalizeTag';
import { TagExplainer } from '../../../app/styles';
import ReadOnlyfieldValueComponent from '../../../app/components/ReadOnlyFieldValueComponent';
import apiRoutes from '../../../../app/api/routes';
import { isValidUrl } from '../../../../app/helpers/isValidUrl';

const FORM_NAME = 'CommunityFormModal';
const REQUIRED_FIELDS = ['name', 'tag', 'description', 'url', 'themeName', 'defaultLanguageId'];

const validate = (values, { t }) => {
  let errors = {};
  errors = generateErrorsForRequiredFields(REQUIRED_FIELDS, values, errors);
  if (values.url && !isValidUrl(values.url)) {
    set(errors, 'url', t('mustBeValidUrl'));
  }
  return errors;
};

const CommunityFormModal = ({
  submitProcessing,
  dataLoading,
  data,
  returnRoute,
  onSubmit,
  header,
  editMode,
  languagesFetch,
  timezonesOverviewFetch,
}) => {
  const { t } = useTranslation();

  const languageOptions = useMemo(
    () =>
      sortBy(languagesFetch.value || [], 'name').map(l => ({
        key: l.id,
        value: l.id,
        text: l.nativeName,
      })),
    [languagesFetch.value]
  );

  const timezoneOptions = useMemo(
    () => (timezonesOverviewFetch.value || []).map(v => ({ key: v, value: v, text: v })),
    [timezonesOverviewFetch.value]
  );

  return (
    <FormModal
      closeIcon
      header={header}
      dataLoading={dataLoading}
      submitProcessing={submitProcessing}
      onSubmit={onSubmit}
      data={data}
      t={t}
      validate={validate}
      form={FORM_NAME}
      cancelRoute={returnRoute}
      cancelButtonContent={t('cancel')}
      saveButtonIcon="save"
      saveButtonContent={t('save')}
    >
      <Grid>
        {!editMode && (
          <Grid.Column mobile={16} tablet={16} computer={16}>
            <Field
              fluid
              label={t('fixedCommunityId')}
              name="id"
              valueComponent={InputFieldValueComponent}
              disabled={submitProcessing || dataLoading}
            />
          </Grid.Column>
        )}
        <Grid.Column mobile={8} tablet={8} computer={8}>
          <Field
            fluid
            label={t('name')}
            name="name"
            valueComponent={InputFieldValueComponent}
            disabled={submitProcessing || dataLoading}
          />
        </Grid.Column>
        <Grid.Column mobile={8} tablet={8} computer={8}>
          <Field
            fluid
            label={
              editMode ? (
                t('tag')
              ) : (
                <span>
                  {t('tag')}
                  &nbsp;
                  <TagExplainer>({t('tagExplainer')})</TagExplainer>
                </span>
              )
            }
            name="tag"
            normalize={normalizeTag}
            valueComponent={editMode ? ReadOnlyfieldValueComponent : InputFieldValueComponent}
            disabled={submitProcessing || dataLoading}
          />
        </Grid.Column>
        <Grid.Column mobile={8} tablet={8} computer={8}>
          <Field
            fluid
            label={t('description')}
            name="description"
            valueComponent={InputFieldValueComponent}
            disabled={submitProcessing || dataLoading}
          />
        </Grid.Column>
        <Grid.Column mobile={8} tablet={8} computer={8}>
          <Field
            fluid
            label={t('url')}
            name="url"
            valueComponent={InputFieldValueComponent}
            disabled={submitProcessing || dataLoading}
          />
        </Grid.Column>
        <Grid.Column mobile={8} tablet={8} computer={8}>
          <Field
            fluid
            label={t('themeName')}
            name="themeName"
            valueComponent={InputFieldValueComponent}
            disabled={submitProcessing || dataLoading}
          />
        </Grid.Column>
        <Grid.Column mobile={8} tablet={8} computer={8}>
          <Field
            fluid
            clearable
            search
            label={t('defaultLanguage')}
            name="defaultLanguageId"
            options={languageOptions}
            disabled={submitProcessing || dataLoading}
            valueComponent={SelectFieldValueComponent}
          />
        </Grid.Column>
        <Grid.Column mobile={8} tablet={8} computer={8}>
          <Field
            fluid
            label={t('tenantHostUrl')}
            name="tenantHostUrl"
            disabled={submitProcessing || dataLoading}
            valueComponent={InputFieldValueComponent}
          />
        </Grid.Column>
        <Grid.Column mobile={8} tablet={8} computer={8}>
          <Field
            fluid
            search
            clearable
            label={t('timezone')}
            name="timeZone"
            disabled={submitProcessing || dataLoading}
            options={timezoneOptions}
            valueComponent={SelectFieldValueComponent}
          />
        </Grid.Column>
      </Grid>
    </FormModal>
  );
};

CommunityFormModal.propTypes = {
  languagesFetch: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  dataLoading: PropTypes.bool,
  submitProcessing: PropTypes.bool,
  header: PropTypes.string.isRequired,
  returnRoute: PropTypes.string.isRequired,
  timezonesOverviewFetch: PropTypes.object.isRequired,
  appInstallations: PropTypes.array,
  editMode: PropTypes.bool,
  data: PropTypes.object,
};

CommunityFormModal.defaultProps = {
  dataLoading: false,
  submitProcessing: false,
  editMode: false,
  appInstallations: [],
  data: {},
};

export default compose(
  lookupService({
    resource: 'languages',
    url: apiRoutes.getLanguages(),
  }),
  lookupService({
    resource: 'timezonesOverview',
    url: apiRoutes.getTimezones(),
  })
)(CommunityFormModal);
